import { Injectable, inject } from '@angular/core';
import Swal, { SweetAlertOptions } from 'sweetalert2';

import { TranslationService } from './translation.service';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    private readonly translationService = inject(TranslationService);

    /**
     * Displays an alert.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     * @param [isHtml] Whether the content has HTML.
     */
    showAlert(message: string, title?: string, isTranslated = false, isHtml = false): void {
        const opt: SweetAlertOptions = { icon: 'warning' };

        if (isTranslated || isHtml) {
            if (isHtml) {
                opt.html = message;
            } else {
                opt.text = message;
            }
        } else {
            this.translationService.getText(message).subscribe(v => (opt.text = v));
        }

        if (!title) {
            this.translationService.getText('Alert.Warning').subscribe(v => (opt.title = v));
        } else {
            this.translationService.getText(title).subscribe(v => (opt.title = v));
        }

        Swal.fire(opt);
    }

    /**
     * Displays an information.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     * @param [isHtml] Whether the content has HTML.
     */
    showInfo(message: string, title?: string, isTranslated = false, isHtml = false): void {
        const opt: SweetAlertOptions = { icon: 'info' };

        if (isTranslated || isHtml) {
            if (isHtml) {
                opt.html = message;
            } else {
                opt.text = message;
            }
        } else {
            this.translationService.getText(message).subscribe(v => (opt.text = v));
        }

        if (!title) {
            this.translationService.getText('Alert.Info').subscribe(v => (opt.title = v));
        } else {
            this.translationService.getText(title).subscribe(v => (opt.title = v));
        }

        Swal.fire(opt);
    }

    /**
     * Displays an error.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     * @param [isHtml] Whether the content has HTML.
     */
    showError(message: string, title?: string, isTranslated = false, isHtml = false): void {
        const opt: SweetAlertOptions = { icon: 'error' };

        if (isTranslated || isHtml) {
            if (isHtml) {
                opt.html = message;
            } else {
                opt.text = message;
            }
        } else {
            this.translationService.getText(message).subscribe(v => (opt.text = v));
        }

        if (!title) {
            this.translationService.getText('Alert.Error').subscribe(v => (opt.title = v));
        } else {
            this.translationService.getText(title).subscribe(v => (opt.title = v));
        }

        Swal.fire(opt);
    }

    /**
     * Displays a success message.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     * @param [callback] Callback executed if the user clicks on the button.
     * @param [isHtml] Whether the content has HTML.
     */
    showSuccess(message: string, title?: string, callback?: any, isTranslated = false, isHtml = false): void {
        const opt: SweetAlertOptions = { icon: 'success' };

        if (isTranslated || isHtml) {
            if (isHtml) {
                opt.html = message;
            } else {
                opt.text = message;
            }
        } else {
            this.translationService.getText(message).subscribe(v => (opt.text = v));
        }

        if (!title) {
            this.translationService.getText('Alert.Success').subscribe(v => (opt.title = v));
        } else {
            this.translationService.getText(title).subscribe(v => (opt.title = v));
        }

        Swal.fire(opt)
            .then(() => {
                if (callback) {
                    callback();
                }
            })
            .catch(() => {});
    }

    /**
     * Displays a message requesting a confirmation from the user.
     * @param message Message to be displayed to the user.
     * @param [title] Message's title.
     * @param [isTranslated] Whether the message/title combo is translated or not.
     * @param [callback] Callback executed if the user clicks on confirm.
     * @param [isHtml] Whether the content has HTML.
     */
    showConfirm(message: string, title?: string, callback?: () => void, isTranslated = false, isHtml = false): void {
        const opt: SweetAlertOptions = { icon: 'warning' };

        if (isTranslated || isHtml) {
            if (isHtml) {
                opt.html = message;
            } else {
                opt.text = message;
            }
        } else {
            this.translationService.getText(message).subscribe(v => (opt.text = v));
        }

        if (!title) {
            this.translationService.getText('Alert.Confirm').subscribe(v => (opt.title = v));
        } else {
            this.translationService.getText(title).subscribe(v => (opt.title = v));
        }

        this.translationService.getText('Alert.CancelButtonText').subscribe(v => (opt.cancelButtonText = v));
        this.translationService.getText('Alert.ConfirmButtonText').subscribe(v => (opt.confirmButtonText = v));

        opt.showCancelButton = true;
        opt.allowEscapeKey = false;
        opt.allowOutsideClick = false;

        Swal.fire(opt)
            .then(result => {
                if (!!result.value && !!callback) {
                    callback();
                }
            })
            .catch(() => {});
    }
}
